import { cmxTypeguards, commonActions, loggerFactory } from '@codametrix/ui-common';
import { Action as ReduxStandardAction, Dispatch } from 'redux';
import { IS_PRODUCTION } from '../environment';
import { recordActivity } from '../session-activity';

const logger = loggerFactory('ParentWindowSubscriptions');

type ChannelReduxAction = ReduxStandardAction & {
  meta?: {
    broadcast?: boolean;
    suppressRedux?: boolean;
  };
};

const activityEvents = new Set<string>([
  `${commonActions.AuthenticatedWindow.ACTIVITY}`
]);

const isBroadcast = (item: ReduxStandardAction) => {
  const action = item as ChannelReduxAction;
  return (cmxTypeguards.isReduxAction(item) && action.meta?.broadcast) ?? false;
};

const senderSubscriptions: CMx.ChannelSubscription[] = [
  {
    match: isBroadcast
  },
  {
    observe: (item: ChannelReduxAction) => {
      if (isBroadcast(item)) {
        logger.debug(`Broadcasting: ${item.type}`, item);
      }
    }
  }
];

const createChildWindowActivityObserver = (
  dispatch: Dispatch<ReduxStandardAction>
): CMx.SubscriptionObserver => ({
  observe: (item: ReduxStandardAction) => {
    if (cmxTypeguards.isReduxAction(item)) {
      const action = item as ChannelReduxAction;
      const suppressRedux = action.meta?.suppressRedux ?? false;
      if (suppressRedux) {
        if (activityEvents.has(item.type)) {
          logger.log(`ChildWindowActivityObserver`, item);
          // Peer ACTIVITY used to only setIsActive (renew latch). Heartbeat uses
          // isActiveSince/_lastActivityAt, so the opener never POSTed /session/heartbeat
          // while the user worked only in a popup. recordActivity arms both.
          recordActivity();
        }
      } else {
        if (action.meta) {
          action.meta.broadcast = false;
        }
        dispatch(item as ReduxStandardAction);
      }
      return false;
    }
  }
});

const createReceiverSubscriptions = (
  dispatch: Dispatch<ReduxStandardAction>
): CMx.ChannelSubscription[] => {
  // The dashboard store owns the dispatch function and injects it here after
  // creation. This keeps channel subscription definitions independent from the
  // store singleton and avoids a module cycle during bootstrap.
  const receiverSubscriptions: CMx.ChannelSubscription[] = [
    createChildWindowActivityObserver(dispatch)
  ];

  if (!IS_PRODUCTION) {
    receiverSubscriptions.push({
      observe: (msg: ReduxStandardAction) => {
        logger.log(`message received`, msg);
      }
    });
  }

  return receiverSubscriptions;
};

export { senderSubscriptions, createReceiverSubscriptions };
